import { cn } from '@utils/cn';
import React from 'react';
import { useTranslation } from '@hooks/useTranslation';

export type StockStatus = 'in_stock' | 'low_stock' | 'out_of_stock';

interface StockBadgeProps {
  status: StockStatus;
  quantity?: number;
  reorderPoint?: number;
  size?: 'sm' | 'md' | 'lg';
  showQuantity?: boolean;
  className?: string;
}

const getStatusFromQuantity = (
  quantity: number,
  reorderPoint?: number,
): StockStatus => {
  if (quantity <= 0) return 'out_of_stock';
  if (reorderPoint && quantity <= reorderPoint) return 'low_stock';
  return 'in_stock';
};

export const StockBadge: React.FC<StockBadgeProps> = ({
  status,
  quantity,
  reorderPoint,
  size = 'md',
  showQuantity = false,
  className = '',
}) => {
  // Sprint 30, story-152. Text-only fix, same badge-atom-family pattern story-148 already
  // established for TransactionTypeBadge/EquipmentStatus/etc. — colors/icons unchanged.
  const tEnumLabels = useTranslation('enumLabels');

  // If quantity is provided, calculate status from it
  const effectiveStatus =
    quantity !== undefined ? getStatusFromQuantity(quantity, reorderPoint) : status;

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs h-5',
    md: 'px-3 py-1 text-sm h-6',
    lg: 'px-4 py-1.5 text-base h-8',
  };

  const statusConfig: Record<StockStatus, { label: string; classes: string; dotColor: string }> = {
    in_stock: {
      label: tEnumLabels.stockStatusInStock,
      classes: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100',
      dotColor: 'bg-green-500',
    },
    low_stock: {
      label: tEnumLabels.stockStatusLowStock,
      classes: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-100',
      dotColor: 'bg-yellow-500',
    },
    out_of_stock: {
      label: tEnumLabels.stockStatusOutOfStock,
      classes: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-100',
      dotColor: 'bg-red-500',
    },
  };

  const config = statusConfig[effectiveStatus];
  const baseClasses =
    'inline-flex items-center justify-center gap-1.5 rounded-full font-medium whitespace-nowrap w-fit';

  const displayText = showQuantity && quantity !== undefined
    ? `${quantity}`
    : config.label;

  return (
    <span
      className={cn(baseClasses, sizeClasses[size], config.classes, className)}
      title={showQuantity ? config.label : undefined}>
      <span className={cn('w-2 h-2 rounded-full', config.dotColor)} />
      {displayText}
    </span>
  );
};

// Helper function to determine stock status
export const calculateStockStatus = getStatusFromQuantity;
